import aceleresIntegrations from 'dashboard/api/aceleres/integrations';

// A guarda de rota e o onMounted da pagina pedem o mesmo payload. Sem cache a
// tela abre com "Carregando conexoes..." mesmo quando o prefetch da guarda ja
// trouxe tudo; sem deduplicacao viram duas requisicoes por abertura.
//
// O cache e por conta: `accountId` diferente e outro conjunto de conexoes.
const cacheByAccount = new Map();
const inFlightByAccount = new Map();
const failureByAccount = new Map();

const DEFAULT_FAILURE_MESSAGE =
  'Não foi possível carregar as integrações. Atualize a página e tente novamente.';

const fetchAll = async accountId => {
  const whatsapp = await aceleresIntegrations.getWhatsapp(accountId);

  let sellers = [];
  const canAssignResponsible =
    whatsapp?.viewer_is_manager ||
    whatsapp?.viewer_can_create ||
    whatsapp?.instances?.some(instance => instance.can_manage);

  if (canAssignResponsible) {
    // A lista de responsáveis é auxiliar: uma falha nela não pode esconder as
    // conexões WhatsApp nem a permissão de criar uma nova conexão.
    const sellersData = await aceleresIntegrations
      .getWhatsappSellers(accountId)
      .catch(() => ({ sellers: [] }));
    sellers = sellersData.sellers || [];
  }

  const payload = { accountId: String(accountId ?? ''), whatsapp, sellers };
  cacheByAccount.set(payload.accountId, payload);
  return payload;
};

export const getIntegracoesPageData = accountId =>
  cacheByAccount.get(String(accountId ?? '')) || null;

export const getIntegracoesPageFailure = accountId =>
  failureByAccount.get(String(accountId ?? '')) || null;

export const clearIntegracoesPageFailures = () => failureByAccount.clear();

export const getIntegracoesPageFailureMessage = failure => {
  if (failure?.code === 'ACTING_USER_NOT_MAPPED') {
    return 'Seu usuário do Zoraxy não está vinculado à empresa no Acelerity. Peça ao administrador para revisar o vínculo.';
  }

  if (failure?.code === 'ACTING_USER_REQUIRED') {
    return 'Não foi possível identificar seu usuário no Zoraxy. Atualize a página e tente novamente.';
  }

  if (failure?.code === 'COMPANY_NOT_FOUND') {
    return 'Esta conta do Zoraxy não está vinculada à empresa no Acelerity. Peça ao administrador para revisar a configuração.';
  }

  if (failure?.httpStatus === 401) {
    return 'Sua sessão do Zoraxy pode ter expirado. Atualize a página e entre novamente.';
  }

  if (failure?.httpStatus === 403) {
    return 'Seu usuário não tem acesso às integrações desta conta. Peça ao administrador para revisar as permissões.';
  }

  if (failure?.httpStatus >= 500) {
    return 'O serviço de integrações está temporariamente indisponível. Tente novamente em instantes.';
  }

  return DEFAULT_FAILURE_MESSAGE;
};

export const hasIntegracoesPageData = accountId =>
  Boolean(getIntegracoesPageData(accountId));

// `catch` silencioso de proposito: uma falha de rede nao pode travar a
// navegacao. A pagina refaz a chamada e ai sim mostra o erro ao utilizador.
export const prefetchIntegracoesPageData = accountId => {
  const accountKey = String(accountId ?? '');
  if (inFlightByAccount.has(accountKey)) {
    return inFlightByAccount.get(accountKey);
  }

  failureByAccount.delete(accountKey);

  const request = fetchAll(accountKey)
    .then(payload => payload)
    .catch(error => {
      failureByAccount.set(accountKey, {
        code: typeof error?.code === 'string' ? error.code : null,
        httpStatus: Number.isInteger(error?.httpStatus)
          ? error.httpStatus
          : null,
      });
      return null;
    })
    .finally(() => {
      inFlightByAccount.delete(accountKey);
    });

  inFlightByAccount.set(accountKey, request);
  return request;
};

export const clearIntegracoesPageData = () => {
  cacheByAccount.clear();
};
